import { useState } from 'react'
import type { Navigate } from '../App'
import { useActiveIslands, useActiveScenarios, useStore } from '../state/store'
import { languageOf } from '../lib/languages'
import { levelFor, scaleStep, scenarioLevel } from '../lib/levels'
import type { Scenario } from '../types'

export function ScenariosView({ navigate }: { navigate: Navigate }) {
  const { data } = useStore()
  const lang = languageOf(data.settings.activeLanguage)
  const ui = lang.ui
  const all = useActiveScenarios()
  const islands = useActiveIslands()
  const level = levelFor(data.settings, data.settings.activeLanguage)

  // Collapsed on every visit, like the islands screen: the level you are on
  // should decide what the screen shows.
  const [showAhead, setShowAhead] = useState(false)

  /*
   * The same split the islands screen makes, on a level a scenario does not
   * store.
   *
   * A scenario is exactly as hard as the hardest island it makes you reach
   * for, so the level is derived rather than kept — one fewer number to hold
   * true, and it stays right when an island is re-levelled or the learner
   * rewrites one. Sending someone into a conversation that demands an island
   * they have never drilled fails them at the one thing this screen is for.
   */
  const ready = all.filter((s) => scenarioLevel(s, islands) <= level)
  const ahead = all.filter((s) => scenarioLevel(s, islands) > level)

  const renderList = (list: Scenario[]) => (
    <div className="stack">
      {list.map((scenario) => {
        const missing = scenario.beats.filter(
          (b) => !islands.some((i) => i.id === b.expectIslandId),
        ).length

        return (
          <div key={scenario.id} className="card">
            <div className="card-head">
              <div>
                <div className="hanzi" style={{ fontSize: '1.35rem' }}>
                  {scenario.titleZh}
                </div>
                <div className="muted" style={{ fontSize: '0.9rem' }}>
                  {scenario.title}
                </div>
              </div>
              <div className="row">
                <span className="badge">{scenario.beats.length} beats</span>
                {scenario.stats.lastScore !== null && (
                  <span className="badge">last {scenario.stats.lastScore}</span>
                )}
              </div>
            </div>

            <div className="hanzi muted" style={{ fontSize: '0.88rem', marginBottom: '0.4rem' }}>
              {scenario.setting}
            </div>
            <p className="english" style={{ marginTop: 0 }}>
              {scenario.brief}
            </p>

            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="data muted" style={{ fontSize: '0.76rem' }}>
                {scenario.stats.runs} runs
                {scenario.stats.bestScore > 0 && ` · best ${scenario.stats.bestScore}`}
              </span>
              <button
                type="button"
                className="btn is-small is-live"
                onClick={() => navigate({ view: 'scenario', id: scenario.id })}
                disabled={scenario.beats.length === 0}
              >
                Walk in
              </button>
            </div>

            {missing > 0 && (
              <div className="notice" style={{ marginTop: '0.7rem' }}>
                {missing} {missing === 1 ? 'beat expects an island' : 'beats expect islands'} you no
                longer have. Those beats will be skipped.
              </div>
            )}
          </div>
        )
      })}
    </div>
  )

  return (
    <>
      <header className="page-head">
        <div className="eyebrow">Scenarios</div>
        <h1 className="page-title">
          <span className="page-title-zh">{ui.scenarios}</span> Deploy under fire
        </h1>
        <p className="lede">
          Drills tell you which island is coming. Conversations do not. Here somebody speaks first,
          and you have to work out which of your islands answers them — then deliver it.
        </p>
      </header>

      <div className="row" style={{ marginBottom: '1.1rem' }}>
        <button type="button" className="btn is-primary" onClick={() => navigate({ view: 'create' })}>
          Build one with AI
        </button>
      </div>

      {all.length === 0 ? (
        <div className="empty">
          No scenarios yet. Build one from your islands with AI, or restore the starter set from
          Settings.
        </div>
      ) : ready.length === 0 ? (
        <div className="empty">
          Every scenario here leans on islands above {scaleStep(lang.code, level)}. Raise your level
          in the rail, or open one anyway below.
        </div>
      ) : (
        renderList(ready)
      )}

      {ahead.length > 0 && (
        <>
          <div className="divider" style={{ marginTop: '1.6rem' }} />
          {/* Collapsed for the same reason the islands screen collapses its
              own: a level that changes nothing you can see reads as broken. */}
          <button
            type="button"
            className="btn is-small is-quiet"
            aria-expanded={showAhead}
            onClick={() => setShowAhead((v) => !v)}
            style={{ marginTop: '1.1rem' }}
          >
            {showAhead ? 'Hide' : 'Show'} {ahead.length}{' '}
            {ahead.length === 1 ? 'scenario' : 'scenarios'} above {scaleStep(lang.code, level)}
          </button>

          {showAhead && (
            <>
              <p className="muted" style={{ fontSize: '0.86rem', margin: '0.9rem 0 1rem' }}>
                These lean on islands you have not reached yet — a scenario is only as easy as the
                hardest island it makes you reach for.
              </p>
              {renderList(ahead)}
            </>
          )}
        </>
      )}
    </>
  )
}
